import React, { useEffect, useState, useRef, useMemo } from "react";
import { api, formatErr, fmtDate } from "../../api";
import { SectionTitle, Avatar, ProgressBar } from "../../components/Atoms";
import { UploadSimple, InstagramLogo, YoutubeLogo, LinkedinLogo, MapPin, GraduationCap, CheckCircle, WarningCircle, X, IdentificationBadge, CircleNotch } from "@phosphor-icons/react";
import { useAuth } from "../../AuthContext";
import { toast } from "sonner";

const YEAR_OPTIONS = ["1st year","2nd year","3rd year","4th year","5th year","Graduated","N/A"];
const GENDER_OPTIONS = ["Male", "Female", "Prefer not to say"];
const ALLOWED_ID_MIMES = ["image/jpeg", "image/jpg", "image/webp"];

const ID_STATUS_COLORS = {
  "Not Submitted": { c: "#6B7280", bg: "rgba(107,114,128,0.15)" },
  "Pending":       { c: "#F59E0B", bg: "rgba(245,158,11,0.15)" },
  "Verified":      { c: "#A3E635", bg: "rgba(163,230,53,0.15)" },
  "Rejected":      { c: "#EC4899", bg: "rgba(236,72,153,0.15)" },
};

function SkillsPicker({ value, onChange }) {
  const [catalog, setCatalog] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    api.get("/skills").then(({ data }) => setCatalog(data)).catch(() => setCatalog([]));
  }, []);
  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const selected = useMemo(() => new Set(value || []), [value]);
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return catalog.filter(s => !selected.has(s) && (!query || s.toLowerCase().includes(query))).slice(0, 12);
  }, [catalog, q, selected]);

  const add = (s) => { if (selected.has(s)) return; onChange([...(value || []), s]); setQ(""); };
  const remove = (s) => onChange((value || []).filter(x => x !== s));

  return (
    <div ref={boxRef} className="relative">
      <div className="input-base mt-1 flex flex-wrap gap-1.5 items-center min-h-[46px]" onClick={() => setOpen(true)}>
        {(value || []).map(s => (
          <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#22D3EE]/15 text-[#22D3EE] text-xs font-bold" data-testid={`skill-chip-${s}`}>
            {s}
            <button type="button" onClick={(e) => { e.stopPropagation(); remove(s); }} className="hover:text-white" aria-label={`Remove ${s}`}>
              <X size={11} weight="bold" />
            </button>
          </span>
        ))}
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={(value || []).length ? "Add more…" : "Type to search skills…"}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-sm text-white placeholder:text-muted"
          data-testid="skills-input"
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-30 mt-1 left-0 right-0 max-h-56 overflow-y-auto glass p-1 border border-white/10 rounded-2xl" data-testid="skills-dropdown">
          {filtered.map(s => (
            <button key={s} type="button" onClick={() => add(s)} className="w-full text-left px-3 py-1.5 rounded-xl hover:bg-white/10 text-sm text-white" data-testid={`skill-option-${s}`}>
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CollegeIdPanel({ profile, onSaved }) {
  const [front, setFront] = useState(profile.college_id_front || "");
  const [back, setBack]   = useState(profile.college_id_back || "");
  const [busy, setBusy]   = useState(false);
  const status = profile.college_id_status || "Not Submitted";
  const s = ID_STATUS_COLORS[status] || ID_STATUS_COLORS["Not Submitted"];
  const isLocked = status === "Pending" || status === "Verified";

  const upload = async (which, f) => {
    if (!f) return;
    if (!ALLOWED_ID_MIMES.includes(f.type.toLowerCase())) {
      toast.error("Only JPG or WEBP images are allowed");
      return;
    }
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setBusy(true);
    try {
      const form = new FormData(); form.append("file", f);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url;
      if (which === "front") setFront(url); else setBack(url);
      toast.success(`${which} uploaded`);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!front || !back) { toast.error("Upload both front & back images"); return; }
    setBusy(true);
    try {
      await api.post("/me/college-id", { front_url: front, back_url: back });
      toast.success("Submitted for verification");
      onSaved && onSaved();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const imgSrc = (u) => (u ? (u.startsWith("http") ? u : backendBase + u) : "");

  return (
    <div className="glass p-5 space-y-3" data-testid="college-id-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <IdentificationBadge size={18} weight="bold" className="text-[#22D3EE]" />
          <div className="text-white font-bold">College ID Verification</div>
        </div>
        <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider" style={{ color: s.c, background: s.bg }} data-testid="college-id-status">
          {status}
        </span>
      </div>

      {status === "Rejected" && profile.college_id_reason && (
        <div className="p-3 rounded-2xl border border-[#EC4899]/40 bg-[#EC4899]/10" data-testid="college-id-reject-reason">
          <div className="text-[#EC4899] text-xs font-bold flex items-center gap-1"><WarningCircle size={12} weight="bold" /> Rejection reason</div>
          <div className="text-sm text-white mt-1">{profile.college_id_reason}</div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {[["front", front, setFront], ["back", back, setBack]].map(([which, url]) => (
          <div key={which} className="rounded-2xl bg-white/5 border border-white/5 p-3">
            <div className="text-xs uppercase font-bold text-muted tracking-wider">{which} image</div>
            <div className="mt-2 h-32 rounded-xl bg-black/30 flex items-center justify-center overflow-hidden">
              {url ? <img src={imgSrc(url)} alt={which} className="max-h-full max-w-full object-contain" /> : <span className="text-muted text-xs">No image</span>}
            </div>
            <label className="mt-2 btn-ghost w-full flex items-center justify-center gap-1.5 text-xs cursor-pointer">
              <UploadSimple size={12} weight="bold" /> {url ? "Replace" : "Upload"} (jpg/webp)
              <input type="file" hidden accept="image/jpeg,image/webp" disabled={isLocked || busy}
                     onChange={e => upload(which, e.target.files?.[0])} data-testid={`college-id-${which}-input`} />
            </label>
          </div>
        ))}
      </div>

      {!isLocked && (
        <button type="button" disabled={busy || !front || !back} onClick={submit}
                className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="college-id-submit-btn">
          {busy ? <CircleNotch size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />}
          Submit for verification
        </button>
      )}

      {profile.college_id_submitted_at && (
        <div className="text-[11px] text-muted">Last submitted: {fmtDate(profile.college_id_submitted_at)}</div>
      )}
    </div>
  );
}

export default function AmbassadorProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [completion, setCompletion] = useState(0);
  const [pending, setPending] = useState(false);
  const [saving, setSaving] = useState(false);

  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    setLoadError(null);
    try {
      const { data } = await api.get("/me/profile");
      setProfile({ ...data, skills: data.skills || [] });
      setCompletion(data.completion_pct || 0);
      setPending(!!data.pending_approval);
    } catch (e) {
      setLoadError(formatErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const form = new FormData(); form.append("file", f);
    try {
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setProfile(p => ({ ...p, photo: url }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...profile,
        skills: Array.isArray(profile.skills) ? profile.skills : [],
        instagram_followers: Number(profile.instagram_followers || 0),
        age: profile.age ? Number(profile.age) : null,
        currently_in_college: !!profile.currently_in_college,
      };
      ["id","user_id","performance_score","created_at","completion_pct","pending_approval",
       "college_id_front","college_id_back","college_id_status","college_id_reason","college_id_submitted_at","college_id_reviewed_at"]
        .forEach(k => delete payload[k]);
      await api.put(`/ambassadors/${user.ref_id}`, payload);
      toast.success("Profile saved");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  if (loadError) {
    return (
      <div className="glass p-8 flex flex-col items-center gap-3 text-center">
        <WarningCircle size={32} weight="fill" className="text-[#EC4899]" />
        <div className="text-white font-bold">Couldn't load your profile</div>
        <div className="text-muted text-sm">{loadError}</div>
        <button onClick={load} className="btn-primary text-sm px-4 py-2 mt-2">Retry</button>
      </div>
    );
  }

  if (!profile) return <div className="skeleton h-64" />;

  const inCollege = profile.currently_in_college !== false;

  return (
    <div className="space-y-5">
      <SectionTitle title="My Profile" subtitle="The brand sees this. Make it pop." />

      {pending && (
        <div className="glass p-4 border-l-4" style={{ borderLeftColor: "#F59E0B" }} data-testid="pending-approval-banner">
          <div className="flex items-start gap-3">
            <WarningCircle size={20} weight="fill" color="#F59E0B" />
            <div>
              <div className="text-white font-bold">Your profile is pending approval from the TYC Team.</div>
              <div className="text-xs text-muted mt-1">You can edit your profile now. Once the TYC team approves you, you&apos;ll be able to apply to campaigns. For urgent help contact support.</div>
            </div>
          </div>
        </div>
      )}

      <div className="glass p-5 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gradient-to-br from-[#EC4899]/30 to-[#8B5CF6]/10 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <Avatar src={profile.photo} name={profile.name} size={80} />
          <div className="flex-1 min-w-0">
            <div className="text-2xl font-black text-white truncate">{profile.name}</div>
            <div className="text-xs text-muted flex items-center gap-1"><MapPin size={12} /> {profile.city || "—"}{profile.college ? ` · ${profile.college}` : ""}</div>
            <div className="text-xs text-[#A3E635] mt-1 font-bold">Score {profile.performance_score || 0}</div>
          </div>
          <label className="btn-ghost flex items-center gap-1.5 text-sm cursor-pointer">
            <UploadSimple size={14} weight="bold" /> Photo
            <input type="file" hidden accept="image/*" onChange={upload} data-testid="photo-upload" />
          </label>
        </div>
        <div className="relative mt-4">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-muted uppercase tracking-wider font-bold">Profile completion</span>
            <span className="text-white font-black" data-testid="completion-pct">{completion}%</span>
          </div>
          <ProgressBar value={completion} max={100} color={completion === 100 ? "#A3E635" : "#22D3EE"} />
        </div>
      </div>

      <CollegeIdPanel profile={profile} onSaved={load} />

      <form onSubmit={save} className="glass p-5 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          {[["name","Name"],["email","Email"],["phone","Mobile Number"],["city","City"],["college","College Name"],["course","Course"],["profession","Profession"]].map(([k,l]) => (
            <div key={k}>
              <label className="text-xs uppercase font-bold text-muted tracking-wider">{l}</label>
              <input value={profile[k] || ""} onChange={e => setProfile({...profile, [k]:e.target.value})} className="input-base mt-1" data-testid={`profile-${k}`} />
            </div>
          ))}
          <div>
            <label className="text-xs uppercase font-bold text-muted tracking-wider flex items-center gap-1"><GraduationCap size={12} /> Year of Study</label>
            <select value={profile.year_of_study || ""} onChange={e => setProfile({...profile, year_of_study:e.target.value})} className="input-base mt-1" data-testid="profile-year_of_study">
              <option value="">—</option>
              {YEAR_OPTIONS.map(y => <option key={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase font-bold text-muted tracking-wider">Age</label>
            <input type="number" min="0" value={profile.age || ""} onChange={e => setProfile({...profile, age:e.target.value})} className="input-base mt-1" data-testid="profile-age" />
          </div>
          <div>
            <label className="text-xs uppercase font-bold text-muted tracking-wider">Gender</label>
            <select value={profile.gender || ""} onChange={e => setProfile({...profile, gender:e.target.value})} className="input-base mt-1" data-testid="profile-gender">
              <option value="">—</option>
              {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase font-bold text-muted tracking-wider">Currently in College?</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[{l:"Yes", v:true}, {l:"No", v:false}].map(o => (
                <button type="button" key={o.l} onClick={() => setProfile({...profile, currently_in_college:o.v})}
                  data-testid={`profile-incollege-${o.l.toLowerCase()}`}
                  className={`py-2.5 rounded-xl text-sm font-bold border ${inCollege === o.v ? "bg-white/10 border-white/20 text-white" : "bg-white/5 border-white/10 text-muted"}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase font-bold text-muted tracking-wider flex items-center gap-1"><InstagramLogo size={12} /> Instagram</label>
            <input value={profile.instagram || ""} onChange={e => setProfile({...profile, instagram:e.target.value})} placeholder="@handle or URL" className="input-base mt-1" data-testid="profile-instagram" />
          </div>
          <div>
            <label className="text-xs uppercase font-bold text-muted tracking-wider">IG Followers</label>
            <input type="number" min="0" value={profile.instagram_followers || 0} onChange={e => setProfile({...profile, instagram_followers:e.target.value})} className="input-base mt-1" data-testid="profile-followers" />
          </div>
          <div>
            <label className="text-xs uppercase font-bold text-muted tracking-wider flex items-center gap-1"><YoutubeLogo size={12} /> YouTube</label>
            <input value={profile.youtube || ""} onChange={e => setProfile({...profile, youtube:e.target.value})} className="input-base mt-1" data-testid="profile-youtube" />
          </div>
          <div>
            <label className="text-xs uppercase font-bold text-muted tracking-wider flex items-center gap-1"><LinkedinLogo size={12} /> LinkedIn</label>
            <input value={profile.linkedin || ""} onChange={e => setProfile({...profile, linkedin:e.target.value})} className="input-base mt-1" data-testid="profile-linkedin" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase font-bold text-muted tracking-wider">Skills</label>
          <SkillsPicker value={profile.skills} onChange={(next) => setProfile({...profile, skills: next})} />
        </div>
        <div>
          <label className="text-xs uppercase font-bold text-muted tracking-wider">Profile Bio</label>
          <textarea rows={3} value={profile.bio || ""} onChange={e => setProfile({...profile, bio:e.target.value})} className="input-base mt-1" data-testid="profile-bio" />
        </div>
        <button disabled={saving} type="submit" className="btn-primary flex items-center gap-2" data-testid="save-profile-btn">
          <CheckCircle size={16} weight="bold" /> {saving ? "Saving…" : "Save profile"}
        </button>
      </form>
    </div>
  );
}
